import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { StopRequirementsFromControlRoom } from '~/types/resources';
import { processStartService } from '../../services/processStartAPI';
import { processRunService } from '../../services/runAPI';
import { getWorkItemSummaryQueryKey } from '../search/useWorkItemSummaryQuery';
import {
  formatErrorPayload,
  useCurrentWorkspaceQueryKey,
  validateApiResponse,
  withSearchSyncDelay,
} from '../utils';

export enum ProcessRunsErrors {
  GET_PROCESS_RUNS_BY_PROCESS_ERROR = 'GET_PROCESS_RUNS_BY_PROCESS_ERROR',
  GET_WORKSPACE_PROCESS_RUNS_ERROR = 'GET_WORKSPACE_PROCESS_RUNS_ERROR',
  GET_PROCESS_RUN_ERROR = 'GET_PROCESS_RUN_ERROR',
  START_PROCESS_RUN_ERROR = 'START_PROCESS_RUN_ERROR',
  DELETE_PROCESS_RUN_ERROR = 'DELETE_PROCESS_RUN_ERROR',
  DELETE_PROCESS_RUNS_ERROR = 'DELETE_PROCESS_RUNS_ERROR',
  STOP_PROCESS_RUN_ERROR = 'STOP_PROCESS_RUN_ERROR',
  STOP_PROCESS_RUNS_ERROR = 'STOP_PROCESS_RUNS_ERROR',
  COMPLETE_PROCESS_RUNS_ERROR = 'COMPLETE_PROCESS_RUNS_ERROR',
}

export const getProcessRunQueryKey = (processId: string, processRunId: string) => [
  'processRun',
  { processId, processRunId },
];

export function useProcessRunQuery(processId: string, processRunId: string) {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getProcessRunQueryKey(processId, processRunId),
    async () => {
      const response = validateApiResponse(
        await processRunService.getProcessRun({ workspaceId, processId, pRunId: processRunId }),
      );
      return response.data;
    },
    {
      enabled: !!processId && !!processRunId,
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: ProcessRunsErrors.GET_PROCESS_RUN_ERROR,
          }),
        );
      },
    },
  );
}

export function useDeleteProcessRunMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ processId, processRunId }: { processId: string; processRunId: string }) => {
      const response = validateApiResponse(
        await processStartService.deleteProcessRun({
          workspaceId,
          processId,
          pRunId: processRunId,
        }),
      );
      return response.data;
    },
    {
      onSuccess: (_, { processId, processRunId }) => {
        queryClient.invalidateQueries(getProcessRunQueryKey(processId, processRunId));
        withSearchSyncDelay(() => {
          queryClient.invalidateQueries(['indexedProcessRuns']);
        });
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: ProcessRunsErrors.DELETE_PROCESS_RUN_ERROR,
          }),
        );
      },
    },
  );
}

export function useDeleteProcessRunsMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (items: { processId: string; processRunId: string }[]) => {
      const responses = await Promise.all(
        items.map(async ({ processId, processRunId }) =>
          processStartService.deleteProcessRun({ workspaceId, processId, pRunId: processRunId }),
        ),
      );

      const firstFailed = responses.find((response) => !response.ok);
      if (firstFailed?.problem) validateApiResponse(firstFailed);

      return responses.map(({ data }) => data);
    },
    {
      onSuccess: (_, args) => {
        // Manually remove from indexed process runs as it's unknown when search service will receive updates
        args.forEach(({ processId, processRunId }) => {
          queryClient.setQueriesData(
            ['indexedProcessRuns', {}, { processId }],
            (previousData: { processRuns: { id: string }[] } | undefined) => {
              if (!previousData) {
                return previousData;
              }
              return {
                ...previousData,
                processRuns: (previousData.processRuns ?? []).filter(
                  ({ id }) => id !== processRunId,
                ),
              };
            },
          );
        });
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: ProcessRunsErrors.DELETE_PROCESS_RUNS_ERROR,
          }),
        );
      },
    },
  );
}

export function useStopProcessRunMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({
      processId,
      processRunId,
      stopRequirements,
    }: {
      processId: string;
      processRunId: string;
      stopRequirements: StopRequirementsFromControlRoom;
    }) => {
      const response = validateApiResponse(
        await processStartService.stopProcessRun({
          workspaceId,
          processId,
          pRunId: processRunId,
          stopRequirements,
        }),
      );
      return response.data;
    },
    {
      onSuccess: (response, { processId, processRunId }) => {
        if (response && response.accepted) {
          queryClient.invalidateQueries(getProcessRunQueryKey(processId, processRunId));
        }
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: ProcessRunsErrors.STOP_PROCESS_RUN_ERROR,
          }),
        );
      },
    },
  );
}

export function useStopProcessRunsMutation() {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({
      runs,
      stopRequirements,
    }: {
      runs: { processId: string; processRunId: string }[];
      stopRequirements: StopRequirementsFromControlRoom;
    }) => {
      const responses = await Promise.all(
        runs.map(async ({ processId, processRunId }) =>
          processStartService.stopProcessRun({
            workspaceId,
            processId,
            pRunId: processRunId,
            stopRequirements,
          }),
        ),
      );

      const firstFailed = responses.find((response) => !response.ok);
      if (firstFailed?.problem) validateApiResponse(firstFailed);

      return responses.map(({ data }) => data);
    },
    {
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: ProcessRunsErrors.STOP_PROCESS_RUNS_ERROR,
          }),
        );
      },
    },
  );
}

export function useCompleteProcessRunsMutation() {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({
      runs,
      reason,
    }: {
      runs: { processId: string; processRunId: string }[];
      reason?: string;
    }) => {
      const responses = await Promise.all(
        runs.map(async ({ processId, processRunId }) =>
          processStartService.completeProcessRun({
            workspaceId,
            processId,
            pRunId: processRunId,
            completeParams: { reason },
          }),
        ),
      );

      const firstFailed = responses.find((response) => !response.ok);
      if (firstFailed?.problem) validateApiResponse(firstFailed);

      return responses.map(({ data }) => data);
    },
    {
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: ProcessRunsErrors.COMPLETE_PROCESS_RUNS_ERROR,
          }),
        );
      },
    },
  );
}

export function useStartProcessRunMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ processId, payload }: { processId: string; payload?: string }) => {
      const response = validateApiResponse(
        await processStartService.startProcess({ workspaceId, processId, payload }),
      );
      return response.data;
    },
    {
      onSuccess: (_data, params) => {
        withSearchSyncDelay(() => {
          queryClient.invalidateQueries(['indexedProcessRuns']);
          queryClient.invalidateQueries(
            getWorkItemSummaryQueryKey({ processId: params.processId }),
          );
        });
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: ProcessRunsErrors.START_PROCESS_RUN_ERROR,
          }),
        );
      },
    },
  );
}

export function useStartProcessRunWithItemsMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ processId, workItemIds }: { processId: string; workItemIds: string[] }) => {
      const response = validateApiResponse(
        await processStartService.startProcessWithItems({ workspaceId, processId, workItemIds }),
      );
      return response.data;
    },
    {
      onSuccess: (_data, params) => {
        withSearchSyncDelay(() => {
          queryClient.invalidateQueries(['indexedProcessRuns']);
          queryClient.invalidateQueries(
            getWorkItemSummaryQueryKey({ processId: params.processId }),
          );
        });
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: ProcessRunsErrors.START_PROCESS_RUN_ERROR,
          }),
        );
      },
    },
  );
}

export function useStartProcessRunDefaultMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ processId }: { processId: string }) => {
      const response = validateApiResponse(
        await processStartService.startDefaultProcessRun({ workspaceId, processId }),
      );
      return response.data;
    },
    {
      onSuccess: (_data, params) => {
        withSearchSyncDelay(() => {
          queryClient.invalidateQueries(['indexedProcessRuns']);
          queryClient.invalidateQueries(
            getWorkItemSummaryQueryKey({ processId: params.processId }),
          );
        });
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: ProcessRunsErrors.START_PROCESS_RUN_ERROR,
          }),
        );
      },
    },
  );
}

export function useStartProcessRunStorageMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ processId, storages }: { processId: string; storages: string[] }) => {
      const response = validateApiResponse(
        await processStartService.startStorageProcessRun({ workspaceId, processId, storages }),
      );
      return response.data;
    },
    {
      onSuccess: (_data, params) => {
        withSearchSyncDelay(() => {
          queryClient.invalidateQueries(['indexedProcessRuns']);
          queryClient.invalidateQueries(
            getWorkItemSummaryQueryKey({ processId: params.processId }),
          );
        });
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: ProcessRunsErrors.START_PROCESS_RUN_ERROR,
          }),
        );
      },
    },
  );
}
